import { createSignal, onMount, Show, type JSX } from 'solid-js'
import giftMegaV2 from '~/assets/boosting-sheet/gift-mega-v4.png'
import giftPlaceholder from '~/assets/boosting-sheet/gift-placeholder.png'
import giftPlaceholderMega from '~/assets/boosting-sheet/gift-placeholder-mega.png'
import giftSmallV2 from '~/assets/boosting-sheet/gift-small-v3.png'
import lightningSvg from '~/assets/boosting-sheet/lightning.svg'
import reflectionMegaSvg from '~/assets/boosting-sheet/reflection-mega.svg'
import reflectionSmallSvg from '~/assets/boosting-sheet/reflection-small.svg'
import ringMegaInnerSvg from '~/assets/boosting-sheet/ring-mega-inner.svg'
import ringMegaOuterSvg from '~/assets/boosting-sheet/ring-mega-outer.svg'
import ringSmallInnerSvg from '~/assets/boosting-sheet/ring-small-inner.svg'
import ringSmallOuterSvg from '~/assets/boosting-sheet/ring-small-outer.svg'
import stampIconSvg from '~/assets/boosting-sheet/stamp-icon.svg'
import starSvg from '~/assets/boosting-banner/star.svg'
import coinIcon from '~/assets/figma/coin.png'
import gemIcon from '~/assets/figma/gem.png'
import type { BoostingPack } from '~/config'
import { forceSkeleton } from '~/lib/params'
import { m } from '~/paraglide/messages'
import {
  selectPriceLabel,
  useBoostingPackPrice,
} from '~/resources/boosting-pack-prices'
import {
  isPackPurchased,
  useBoostingPackStats,
} from '~/resources/boosting-pack-stats'
import { Spinner } from './spinner'

/** A single reward badge: outer ring + inner ring + icon + amount label,
 *  with an optional bonus tag (+50%) top-left. Matches Figma 4473:39118. */
function RewardBadge(props: {
  tier: 'small' | 'mega'
  icon: JSX.Element
  amount: number
  bonusPercent?: number
}) {
  const outer = () =>
    props.tier === 'mega' ? ringMegaOuterSvg : ringSmallOuterSvg
  const inner = () =>
    props.tier === 'mega' ? ringMegaInnerSvg : ringSmallInnerSvg
  return (
    <div class="relative size-[57px] shrink-0">
      <img
        src={outer()}
        alt=""
        aria-hidden="true"
        class="absolute inset-0 size-[57px]"
      />
      <img
        src={inner()}
        alt=""
        aria-hidden="true"
        class="absolute left-1 top-1 size-[49px]"
      />
      <div class="absolute left-1/2 top-1/2 size-9 -translate-x-1/2 -translate-y-1/2 drop-shadow-[0_2.5px_0.7px_rgba(0,0,0,0.25)]">
        {props.icon}
      </div>
      {/* Faux-gradient outline via 8-direction text-shadow stack — top half
          uses the lighter mauve, bottom half the deeper purple. Pure CSS, no
          SVG, works in every browser including Safari + sandboxed webviews. */}
      <span
        class="absolute bottom-[7px] left-1/2 -translate-x-1/2 translate-y-1/2 whitespace-nowrap text-[15px] font-bold leading-[9.5px] text-white"
        style={{
          'font-family':
            "'SF Pro Rounded', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
          // Small pack uses the deeper purple gradient; mega pack uses the
          // softer mauve gradient (swapped from prior).
          'text-shadow': (props.tier === 'mega'
            ? [
                '-1.5px -1.5px 0 #9379B8',
                '0 -1.5px 0 #9379B8',
                '1.5px -1.5px 0 #9379B8',
                '-1.5px 0 0 #7A5F9F',
                '1.5px 0 0 #7A5F9F',
                '-1.5px 1.5px 0 #614687',
                '0 1.5px 0 #614687',
                '1.5px 1.5px 0 #614687',
              ]
            : [
                '-1.5px -1.5px 0 #A893C9',
                '0 -1.5px 0 #A893C9',
                '1.5px -1.5px 0 #A893C9',
                '-1.5px 0 0 #8E7AB7',
                '1.5px 0 0 #8E7AB7',
                '-1.5px 1.5px 0 #7561A5',
                '0 1.5px 0 #7561A5',
                '1.5px 1.5px 0 #7561A5',
              ]
          ).join(', '),
        }}
      >
        +{props.amount}
      </span>
      <Show when={props.bonusPercent !== undefined}>
        <div class="absolute -top-[9px] left-2 grid h-[18px] place-items-center rounded-full bg-[rgba(219,39,222,0.5)] px-1 backdrop-blur-[6.4px]">
          <span class="whitespace-nowrap text-[10px] font-bold leading-[6.82px] text-white">
            + {props.bonusPercent}%
          </span>
        </div>
      </Show>
    </div>
  )
}

/** Responsive gift box — `flex-1` container shares the row width with the
 *  info container. The illustration PNG is rendered at a fixed width (no
 *  height override) so the browser preserves its natural 352:252 aspect —
 *  the user explicitly asked for no distortion, so we override Figma's
 *  slight horizontal stretch. */
function GiftBox(props: { tier: 'small' | 'mega' }) {
  const src = props.tier === 'mega' ? giftMegaV2 : giftSmallV2
  const isMega = () => props.tier === 'mega'
  const [loaded, setLoaded] = createSignal(false)
  let imgEl: HTMLImageElement | undefined
  // `?skeleton` holds the placeholder for dev preview (see params.ts).
  const reveal = () => {
    if (!forceSkeleton) setLoaded(true)
  }
  // Cached images can finish before the onLoad handler binds — catch that.
  onMount(() => {
    if (imgEl?.complete && imgEl.naturalWidth > 0) reveal()
  })
  return (
    <div class="relative h-[97px] min-w-px flex-[1_0_0] min-[413px]:scale-[1.08] min-[501px]:scale-[1.16]">
      {/* Placeholder — lightweight gift-box illustration shown until the real
          (per-tier) art loads, then it unmounts as the real art fades in.
          Mirrors the real composite's positioning so the swap is seamless. */}
      <Show when={!loaded()}>
        <div
          aria-hidden
          class="pointer-events-none absolute left-[calc(50%-16px)] top-1/2 -translate-x-1/2 -translate-y-1/2 pb-2"
          classList={{ 'scale-90': !isMega() }}
        >
          <img
            src={isMega() ? giftPlaceholderMega : giftPlaceholder}
            alt=""
            aria-hidden="true"
            class="block w-[150px] max-w-none opacity-40 mix-blend-multiply brightness-90"
          />
        </div>
      </Show>
      {/* Small pack's gift box renders at 90% — scale the whole composite
          (illustration + glow + light rays) so the glints stay aligned.
          The whole composite fades in once the illustration has loaded. */}
      <div
        class="absolute left-[calc(50%-16px)] top-1/2 -translate-x-1/2 -translate-y-1/2 pb-2 transition-opacity duration-300"
        classList={{
          'scale-90': !isMega(),
          'opacity-0': !loaded(),
          'opacity-100': loaded(),
        }}
      >
        <img
          ref={imgEl}
          src={src}
          alt=""
          aria-hidden="true"
          onLoad={reveal}
          class="block w-[150px] max-w-none"
        />
        {/* Center highlight — Figma 4535:40138. Yellow radial glow, 87×41,
            blur 8.41px, opacity 0.66, centred on the illustration. */}
        <div
          aria-hidden
          class="pointer-events-none absolute left-1/2 top-1/2 h-[41px] w-[87px] -translate-x-1/2 -translate-y-1/2 mix-blend-plus-lighter"
          style={{
            background:
              'radial-gradient(circle at 32% 90%, rgba(255, 248, 147, 1) 0%, rgba(253, 255, 119, 0) 100%)',
            filter: 'blur(8.41px)',
            opacity: '0.15',
          }}
        />
        {/* Coin-pile glint — the same animated light-ray cross used on the
            gems, placed on the topmost coin of the stack. Out-of-phase
            duration/delay so it sparkles independently from the gem rays.
            Positioned per tier since the two illustrations differ. */}
        <div
          aria-hidden
          class="pointer-events-none absolute mix-blend-plus-lighter [animation:lightRayFlash_3.4s_ease-in-out_infinite_0.9s]"
          style={{
            left: isMega() ? '127px' : '126px',
            top: isMega() ? '66px' : '57px',
          }}
        >
          <div class="absolute h-[1.5px] w-[18px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-transparent via-white to-transparent" />
          <div class="absolute h-[18px] w-[1.5px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-b from-transparent via-white to-transparent" />
          <div class="absolute h-[1px] w-[13px] -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-gradient-to-r from-transparent via-white/60 to-transparent" />
          <div class="absolute h-[1px] w-[13px] -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-gradient-to-r from-transparent via-white/60 to-transparent" />
        </div>
        {/* Primary light ray — gem cluster centre */}
        <div
          aria-hidden
          class="pointer-events-none absolute mix-blend-plus-lighter [animation:lightRayFlash_3.6s_ease-in-out_infinite]"
          style={{
            left: isMega() ? '68px' : '85px',
            top: isMega() ? '33px' : '48px',
          }}
        >
          <div class="absolute h-[2px] w-[24px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-transparent via-white to-transparent" />
          <div class="absolute h-[20px] w-[2px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-b from-transparent via-white to-transparent" />
          <div class="absolute h-[1.5px] w-[16px] -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-gradient-to-r from-transparent via-white/60 to-transparent" />
          <div class="absolute h-[1.5px] w-[16px] -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-gradient-to-r from-transparent via-white/60 to-transparent" />
        </div>
        {/* Secondary light ray — adjacent facet, fires out-of-phase */}
        <div
          aria-hidden
          class="pointer-events-none absolute mix-blend-plus-lighter [animation:lightRayFlash_3.2s_ease-in-out_infinite_1.8s]"
          style={{
            left: isMega() ? '58px' : '75px',
            top: isMega() ? '43px' : '58px',
          }}
        >
          <div class="absolute h-[1.5px] w-[16px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-transparent via-white to-transparent" />
          <div class="absolute h-[16px] w-[1.5px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-b from-transparent via-white to-transparent" />
          <div class="absolute h-[1px] w-[12px] -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-gradient-to-r from-transparent via-white/50 to-transparent" />
          <div class="absolute h-[1px] w-[12px] -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-gradient-to-r from-transparent via-white/50 to-transparent" />
        </div>
        {/* Lightning burst (mega only) — 3 bolts radiate from the box centre
            (the circle's origin) and fade. Only the upper hemisphere fires.
            Angles, travel (`--lb-dist`), size, duration and delay are all
            irregular so the fan looks scattered rather than mechanical. They
            share one duration and fire within a tight delay window so the
            three appear almost together each cycle. Each bolt is offset so
            its centre sits on the rotation origin. */}
        <Show when={isMega()}>
          <div
            aria-hidden
            class="pointer-events-none absolute"
            style={{ left: '76px', top: '42px' }}
          >
            {[
              {
                angle: -47,
                dist: -58,
                w: 10.4,
                delay: 0,
                op: 0.6,
                dx: 0,
                glow: '0 0 5px rgba(255,255,255,0.95)',
              },
              {
                angle: 3,
                dist: -46,
                w: 8,
                delay: 60,
                op: 0.7,
                dx: 0,
                glow: '0 0 2px rgba(255,255,255,0.6)',
              },
              {
                angle: 27,
                dist: -64,
                w: 9.6,
                delay: 120,
                op: 0.75,
                dx: 20,
                glow: '0 0 8px rgba(255,255,255,0.8)',
              },
            ].map((bolt) => (
              <div
                class="absolute top-0"
                style={{
                  left: `${bolt.dx}px`,
                  transform: `rotate(${bolt.angle}deg)`,
                  opacity: bolt.op,
                }}
              >
                <img
                  src={lightningSvg}
                  alt=""
                  aria-hidden="true"
                  class="absolute max-w-none mix-blend-plus-lighter [animation:lightningBurst_2.5s_cubic-bezier(0.22,1,0.36,1)_infinite]"
                  style={{
                    width: `${bolt.w}px`,
                    left: `${-bolt.w / 2}px`,
                    top: `${(-bolt.w * 23) / 12 / 2}px`,
                    '--lb-dist': `${bolt.dist}px`,
                    filter: `drop-shadow(${bolt.glow})`,
                    'animation-delay': `${bolt.delay}ms`,
                  }}
                />
              </div>
            ))}
          </div>
        </Show>
      </div>
    </div>
  )
}

function StarRow(props: { count: number }) {
  return (
    <span class="flex items-center gap-1">
      {Array.from({ length: props.count }).map(() => (
        <img
          src={starSvg}
          alt=""
          aria-hidden="true"
          class="size-[13px] shrink-0"
        />
      ))}
    </span>
  )
}

export function BoostingPackCard(props: {
  pack: BoostingPack
  /** This pack's own purchase is in flight — shows the button spinner. */
  pending: boolean
  /** Any pack purchase is in flight — blocks buying every pack. */
  purchasing: boolean
  onBuy: () => void
}) {
  const stats = useBoostingPackStats(() => props.pack.productId)
  const price = useBoostingPackPrice(props.pack)

  const purchased = () => isPackPurchased(stats.data)
  const priceLabel = () =>
    selectPriceLabel(price.data, props.pack.fallbackPrice)
  // While getPrice is in flight we show a spinner instead of the USD
  // fallback so the store-localized price never "flashes" from USD to the
  // user's currency on first load. The fallback only renders once the query
  // settles without a usable price (failure result or bridge error).
  const priceLoading = () => price.isPending
  // These packs are Limit-1, so until the lifetime purchase stats land we
  // can't tell whether the user already owns this one — `isPackPurchased`
  // reads false for `undefined`. Keep the button disabled (and showing a
  // spinner) until the stats query succeeds, and treat a stats error as
  // "unavailable", so an already-purchased pack can never re-enter the flow.
  const statsLoading = () => stats.isPending
  const statsReady = () => stats.isSuccess
  const disabled = () =>
    purchased() || props.purchasing || priceLoading() || !statsReady()
  const isMega = () => props.pack.tier === 'mega'
  const title = () => (isMega() ? m.booster_pack_mega() : m.booster_pack_s())

  return (
    <div class="relative w-full overflow-clip rounded-[30px] shadow-[0_-7px_10.6px_0_rgba(255,186,222,0.48)]">
      {/* Card gradient bg per Figma 248.8deg. */}
      <div
        aria-hidden
        class="pointer-events-none absolute inset-0 rounded-[30px]"
        style={{
          'background-image': isMega()
            ? 'linear-gradient(256deg, #7E468D 15.85%, #534BC2 77.14%)'
            : 'linear-gradient(248.8deg, #EDA4FF 15.854%, #7B72F2 77.139%)',
        }}
      />
      {/* Two overlapping reflection rectangles in the upper-right region.
          Rendered BEFORE the gradient border so the border ring stays on top
          and the reflection can't visually overlap the card edge. */}
      <div
        aria-hidden
        class="pointer-events-none absolute left-[15.27%] right-[-12.39%] top-0 aspect-[337/220]"
      >
        <img
          src={isMega() ? reflectionMegaSvg : reflectionSmallSvg}
          alt=""
          aria-hidden="true"
          class="absolute left-[7.72%] right-[-2.97%] top-0 aspect-[321/220]"
        />
        <img
          src={isMega() ? reflectionMegaSvg : reflectionSmallSvg}
          alt=""
          aria-hidden="true"
          class="absolute left-0 right-[4.75%] top-0 aspect-[321/220]"
        />
      </div>
      {/* Gradient ring border — keeps the same pink base (#f4bed2) but
          adds bright highlights on opposite corners for a reflective sheen.
          Rendered via the mask-composite trick: paint gradient on the full
          border-box, then exclude the inner content-box so only the 2px
          ring remains. Stays on top so the reflection layer can't bleed
          over the card edge. */}
      <div
        aria-hidden
        class="pointer-events-none absolute inset-0 rounded-[30px] p-[2px]"
        style={{
          'background-image':
            'linear-gradient(135deg, #D99FB5 0%, #F4BED2 35%, #FFFFFF 50%, #F4BED2 65%, #D99FB5 100%)',
          mask: 'linear-gradient(white 0 0) content-box, linear-gradient(white 0 0)',
          '-webkit-mask':
            'linear-gradient(white 0 0) content-box, linear-gradient(white 0 0)',
          'mask-composite': 'exclude',
          '-webkit-mask-composite': 'xor',
        }}
      />

      {/* Content — flex flex-col gap-16 px-16 py-12 (Figma 4473:35433). */}
      <div class="relative flex flex-col items-center gap-4 px-4 py-3">
        <div class="flex w-full items-end gap-[11px]">
          <GiftBox tier={props.pack.tier} />
          <div class="flex min-w-0 flex-1 flex-col gap-[9px]">
            <div class="flex w-full items-center justify-end gap-[5px]">
              <StarRow count={isMega() ? 3 : 1} />
              <span class="whitespace-nowrap text-[16px] font-semibold leading-[30px] text-white [text-shadow:0_0_8.9px_#bb51fd]">
                {title()}
              </span>
            </div>
            <div class="flex w-full items-center justify-end gap-3 max-[419px]:gap-1">
              <RewardBadge
                tier={props.pack.tier}
                icon={
                  <img
                    src={stampIconSvg}
                    alt=""
                    aria-hidden="true"
                    class="absolute inset-0 size-full object-contain"
                  />
                }
                amount={props.pack.rewards.cookies}
                bonusPercent={isMega() ? props.pack.bonusPercent : undefined}
              />
              <RewardBadge
                tier={props.pack.tier}
                icon={
                  <img
                    src={coinIcon}
                    alt=""
                    aria-hidden="true"
                    class="absolute inset-0 size-full object-contain"
                  />
                }
                amount={props.pack.rewards.coins}
              />
              <RewardBadge
                tier={props.pack.tier}
                icon={
                  <img
                    src={gemIcon}
                    alt=""
                    aria-hidden="true"
                    class="absolute inset-0 size-full object-contain"
                  />
                }
                amount={props.pack.rewards.gems}
              />
            </div>
          </div>
        </div>

        <div class="flex w-full flex-col items-center gap-[11px]">
          <button
            type="button"
            disabled={disabled()}
            onClick={() => props.onBuy()}
            class="relative flex h-[52px] w-full max-w-[640px] items-center justify-center gap-2 overflow-clip rounded-[16px] border-2 border-[#faea84] bg-[#ffd858] px-3 py-4 text-[16px] font-bold leading-5 text-[#66401a] transition-colors [transition-duration:100ms] active:scale-[0.97] active:border-[#ebac17] active:bg-[rgba(180,140,8,0.7)]"
            classList={{
              'opacity-30': purchased(),
              'opacity-60': !purchased() && props.purchasing,
            }}
          >
            <Show
              when={
                props.pending ||
                (!purchased() && (priceLoading() || statsLoading()))
              }
              fallback={purchased() ? m.purchased_btn() : priceLabel()}
            >
              <Spinner variant="circular" class="size-6 text-[#66401a]" />
            </Show>
            {/* Animated diagonal gloss sweep — same 110° angle as the
                card's reflection. Stripe stays off-screen for 60% of the
                cycle so the gloss pulses rather than runs continuously.
                Mega pack lags small by 0.5s so the two buttons stagger
                instead of pulsing in unison. Suppressed once purchased so
                the disabled state reads as "done" rather than promotional. */}
            <Show when={!purchased()}>
              <span
                aria-hidden
                class="pointer-events-none absolute -inset-y-2 left-0 w-[40%] animate-gloss-sweep"
                style={{
                  'background-image':
                    'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 48%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,0.55) 52%, transparent 70%)',
                  'animation-delay': isMega() ? '0.5s' : '0s',
                }}
              />
            </Show>
          </button>
          <p class="px-3 text-center text-[14px] leading-[1.1] text-white">
            {purchased()
              ? m.Limited_purchase_reached()
              : m.limited_purchase_pp()}
          </p>
        </div>
      </div>
    </div>
  )
}
